import { Link } from '@/components/LocalizedLink';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SEOHead from '@/components/SEOHead';
import NotFound from '@/pages/NotFound';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  getFeaturedServiceSlugs,
  getServiceBySlug,
  getServicesBySlugs,
  localizeServiceText,
} from '@/lib/serviceCatalog';
import { getPillarGuideForServiceSlug, localizePillarGuideText } from '@/lib/pillarGuideCatalog';
import { fetchPublishedBlogPosts, fetchPublishedProjects } from '@/lib/contentService';
import RelatedProjectsBlock from '@/components/knowledge/RelatedProjectsBlock';
import RelatedInsightsBlock from '@/components/knowledge/RelatedInsightsBlock';
import RelatedServicesBlock from '@/components/knowledge/RelatedServicesBlock';
import FAQSection from '@/components/knowledge/FAQSection';
import DirectAnswerBlock from '@/components/knowledge/DirectAnswerBlock';
import { SITE_URL } from '@/lib/seo';
import { buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildSpeakableSchema } from '@/lib/schema';
import RelatedSolutionsBlock from '@/components/knowledge/RelatedSolutionsBlock';
import { getRelatedSolutionSlugsForService, getSolutionsBySlugs } from '@/lib/solutionCatalog';
import RelatedPlaybooksBlock from '@/components/knowledge/RelatedPlaybooksBlock';
import { getAuthorityAssetsBySlugs, getRelatedAuthorityAssetSlugsForService } from '@/lib/authorityAssetCatalog';
import {
  AutomationToolExamplesBlock,
  DeliveryTermsBlock,
  ProposalProcessBlock,
} from '@/components/knowledge/ProposalProcessBlocks';
import {
  CaseStudyLinksBlock,
  ExampleCostBandsBlock,
  GuideLinksBlock,
  LandingSectionsBlock,
  ToolLinksBlock,
} from '@/components/knowledge/LandingBlocks';
import { getCostProfileForService } from '@/lib/proposalProcess';
import TrustFactsBlock from '@/components/TrustFactsBlock';
import { LANDING_COPY, resolveLandingText } from '@/lib/landingContent';

const ServiceDetailPage = () => {
  const { lang } = useLanguage();
  const { slug } = useParams<{ slug: string }>();
  const service = slug ? getServiceBySlug(slug) : null;

  const { data: projects } = useQuery({
    queryKey: ['services', 'detail', slug, 'projects'],
    queryFn: fetchPublishedProjects,
    enabled: Boolean(service),
  });

  const { data: posts } = useQuery({
    queryKey: ['services', 'detail', slug, 'posts'],
    queryFn: fetchPublishedBlogPosts,
    enabled: Boolean(service),
  });

  if (!service) return <NotFound />;

  const relatedProjects = service
    ? service.relatedProjectSlugs
        .map((projectSlug) => (projects || []).find((item) => item.slug === projectSlug))
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .slice(0, 3)
    : [];

  const relatedPosts = service
    ? service.relatedBlogSlugs
        .map((postSlug) => (posts || []).find((item) => item.slug === postSlug))
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .slice(0, 3)
    : [];

  const relatedServices = getServicesBySlugs(
    getFeaturedServiceSlugs().filter((item) => item !== service.slug),
  ).slice(0, 2);
  const relatedSolutions = getSolutionsBySlugs(getRelatedSolutionSlugsForService(service.slug)).slice(0, 3);
  const relatedPlaybooks = getAuthorityAssetsBySlugs(getRelatedAuthorityAssetSlugsForService(service.slug)).slice(0, 3);

  const faqSchema = buildFaqSchema(
    service.faqs.map((item) => ({
      question: localizeServiceText(item.question, lang),
      answer: localizeServiceText(item.answer, lang),
    })),
    {
      inLanguage: lang === 'tr' ? 'tr-TR' : 'en-US',
      canonicalPath: `/services/${service.slug}`,
      locale: lang,
    },
  );

  const serviceSchema = buildServiceSchema({
    name: localizeServiceText(service.navTitle, lang),
    description: localizeServiceText(service.seoDescription, lang),
    serviceType: localizeServiceText(service.shortLabel, lang),
    slug: service.slug,
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: lang === 'tr' ? 'Ana Sayfa' : 'Home', item: SITE_URL },
    { name: lang === 'tr' ? 'Hizmetler' : 'Services', item: `${SITE_URL}/services` },
    { name: localizeServiceText(service.navTitle, lang), item: `${SITE_URL}/services/${service.slug}` },
  ]);

  const speakableSchema = buildSpeakableSchema({
    canonicalPath: `/services/${service.slug}`,
    locale: lang,
  });
  const landing = service.landing;
  const directAnswer = landing
    ? resolveLandingText(localizeServiceText(landing.quickAnswer, lang), lang)
    : localizeServiceText(service.intro, lang);
  const pillarGuide = getPillarGuideForServiceSlug(service.slug);
  const linksToCostEstimator = !landing && service.relatedToolIds.includes('project-cost-estimator');

  return (
    <main className="pt-32 pb-20">
      <SEOHead
        title={localizeServiceText(service.seoTitle, lang)}
        description={localizeServiceText(service.seoDescription, lang)}
        canonical={`/services/${service.slug}`}
        jsonLd={[serviceSchema, breadcrumbSchema, faqSchema, speakableSchema]}
      />

      <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">
          {lang === 'tr' ? 'Global Product Engineering Service' : 'Global Product Engineering Service'}
        </p>
        <h1 className="font-display text-4xl lg:text-6xl font-bold text-foreground max-w-4xl leading-[1.05] mb-6">
          {localizeServiceText(service.heroTitle, lang)}
        </h1>
        <p className="font-body text-lg text-muted-foreground leading-relaxed max-w-3xl mb-8">
          {localizeServiceText(service.intro, lang)}
        </p>

        <DirectAnswerBlock
          answer={directAnswer}
          label={landing ? LANDING_COPY.quickAnswerLabel[lang] : undefined}
          className="max-w-3xl mb-10"
        />

        {pillarGuide && (
          <p className="font-body text-sm text-muted-foreground mb-10 max-w-3xl">
            {lang === 'tr' ? 'Derinlemesine rehber: ' : 'In-depth guide: '}
            <Link
              to={`/guides/${pillarGuide.slug}`}
              className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors"
            >
              {localizePillarGuideText(pillarGuide.title, lang)}
            </Link>
          </p>
        )}

        {linksToCostEstimator ? (
          <p className="font-body text-sm text-muted-foreground mb-10 max-w-3xl leading-relaxed">
            {lang === 'tr'
              ? 'Örnek TRY bütçe bantları (KDV hariç, premium butik ajans kalibrasyonu) ve kapsam dışı kalemler '
              : 'Illustrative TRY budget bands (excl. VAT, premium boutique calibration) and out-of-scope items '}
            <Link
              to="/tools/project-cost-estimator"
              className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors"
            >
              {lang === 'tr' ? 'proje maliyet tahmincisi' : 'project cost estimator'}
            </Link>
            {lang === 'tr'
              ? ' aracında gösterilir; sabit fiyat teklifi değildir — kesin fiyat teklif sürecimizde hesaplanır.'
              : ' tool; not a fixed quote — the final price is calculated in our proposal process.'}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-3 mb-12">
          {service.taxonomy.capabilities.map((capability) => (
            <span key={capability} className="font-body text-xs bg-secondary rounded-full px-3 py-1 text-muted-foreground">
              {capability}
            </span>
          ))}
        </div>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Kimler İçin' : 'Who This Is For'}
            </h2>
            <ul className="space-y-2">
              {service.idealFor.map((item) => (
                <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
              ))}
            </ul>
          </article>

          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Çözdüğümüz Problemler' : 'Problems We Solve'}
            </h2>
            <ul className="space-y-2">
              {service.problems.map((item) => (
                <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
              ))}
            </ul>
          </article>
        </section>

        {landing ? <LandingSectionsBlock lang={lang} landing={landing} className="mt-10" /> : null}

        <section className="mt-10 rounded-2xl border border-border/70 p-6 bg-card/40">
          <h2 className="font-display text-2xl text-foreground mb-3">
            {lang === 'tr' ? 'Ne Geliştiriyoruz / Ne Teslim Ediyoruz' : 'What We Build / Deliver'}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {service.deliverables.map((item) => (
              <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
            ))}
          </ul>
        </section>

        <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Tipik Proje Tipleri' : 'Typical Project Types'}
            </h2>
            <ul className="space-y-2">
              {service.taxonomy.projectTypes.map((item) => (
                <li key={item} className="font-body text-sm text-muted-foreground">• {item}</li>
              ))}
            </ul>
          </article>
          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Zamanlama Beklentisi' : 'Timeline Expectations'}
            </h2>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">
              {lang === 'tr'
                ? 'Teslimat takvimi kapsam, ekip büyüklüğü ve teknik bağımlılıklara göre değişir. Net takvimi teklif sürecinde iş listesi ve efor tahminiyle birlikte, sprint bazlı olarak yazılı teklifte paylaşıyoruz.'
                : 'Timeline depends on scope, team shape, and technical dependencies. We share a sprint-based timeline in the written proposal, built from the task list and effort estimate.'}
            </p>
          </article>
        </section>

        <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Çalışma Modeli' : 'Engagement Models'}
            </h2>
            <ul className="space-y-2">
              {service.engagementModels.map((item) => (
                <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
              ))}
            </ul>
          </article>

          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Neden Figtures' : 'Why Figtures'}
            </h2>
            <ul className="space-y-2">
              {service.differentiators.map((item) => (
                <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
              ))}
            </ul>
          </article>
        </section>

        <section className="mt-10 rounded-2xl border border-border/70 p-6 bg-card/40">
          <h2 className="font-display text-2xl text-foreground mb-4">
            {lang === 'tr' ? 'Delivery Approach' : 'Delivery Approach'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {service.process.map((step) => (
              <article key={step.title.en} className="rounded-xl border border-border/50 p-4 bg-background">
                <h3 className="font-display text-lg text-foreground mb-1">{localizeServiceText(step.title, lang)}</h3>
                <p className="font-body text-sm text-muted-foreground">{localizeServiceText(step.description, lang)}</p>
              </article>
            ))}
          </div>
        </section>

        {landing ? (
          <>
            <ProposalProcessBlock lang={lang} className="mt-10" />
            <ExampleCostBandsBlock lang={lang} landing={landing} className="mt-10" />
            <DeliveryTermsBlock lang={lang} profile={null} className="mt-10" />
          </>
        ) : (
          <DeliveryTermsBlock lang={lang} profile={getCostProfileForService(service.slug)} showCoverage className="mt-10" />
        )}

        {service.slug === 'ai-automation-agents' ? (
          <AutomationToolExamplesBlock lang={lang} className="mt-10" />
        ) : null}

        <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Kimler İçin Uygun' : 'Best Fit'}
            </h2>
            <ul className="space-y-2">
              {service.fitFor.map((item) => (
                <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
              ))}
            </ul>
          </article>

          <article className="rounded-2xl border border-border/70 p-6 bg-card/40">
            <h2 className="font-display text-2xl text-foreground mb-3">
              {lang === 'tr' ? 'Uygun Olmayabilecek Durumlar' : 'When We May Not Be the Right Fit'}
            </h2>
            <ul className="space-y-2">
              {service.notFitFor.map((item) => (
                <li key={item.en} className="font-body text-sm text-muted-foreground">• {localizeServiceText(item, lang)}</li>
              ))}
            </ul>
          </article>
        </section>

        {landing ? (
          <>
            <TrustFactsBlock lang={lang} className="mt-16" />
            <CaseStudyLinksBlock landing={landing} />
            <ToolLinksBlock lang={lang} toolIds={service.relatedToolIds} />
            {landing.guideSlugs ? <GuideLinksBlock lang={lang} slugs={landing.guideSlugs} /> : null}
          </>
        ) : (
          <RelatedProjectsBlock projects={relatedProjects} title={lang === 'tr' ? 'Bu Hizmetle İlgili Projeler' : 'Projects Related to This Service'} />
        )}
        <RelatedInsightsBlock posts={relatedPosts} title={lang === 'tr' ? 'Bu Hizmetle İlgili İçerikler' : 'Insights Related to This Service'} />
        <RelatedServicesBlock services={relatedServices} title={lang === 'tr' ? 'Diğer Hizmetler' : 'Other Services'} />
        <RelatedSolutionsBlock
          solutions={relatedSolutions}
          title={lang === 'tr' ? 'İlgili AI Workflow Çözümleri' : 'Related AI Workflow Solutions'}
        />
        <RelatedPlaybooksBlock
          assets={relatedPlaybooks}
          title={lang === 'tr' ? 'Bu Hizmet İçin Karar Rehberleri' : 'Decision Guides for This Service'}
        />

        <FAQSection items={service.faqs} />

        <section className="mt-16 rounded-2xl border border-border p-8 bg-secondary/30 text-center">
          <h2 className="font-display text-3xl text-foreground mb-3">
            {lang === 'tr' ? 'Bu hizmet için bir teslimat planı çıkaralım' : 'Let’s map a delivery plan for this service'}
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto mb-6">
            {lang === 'tr'
              ? 'Ürününüzün hedeflerini, teknik sınırlarını ve ilk sprint kapsamını birlikte netleştirip hızlı bir başlangıç planı oluşturalım.'
              : 'We can align on your product goals, technical constraints, and first sprint scope to define a practical start plan.'}
          </p>
          <Link to="/contact" className="inline-flex px-7 py-3 rounded-full bg-foreground text-background font-body text-sm hover:opacity-90 transition-opacity">
            {lang === 'tr' ? 'Projeni Konuşalım' : 'Discuss Your Project'}
          </Link>
        </section>
      </div>
    </main>
  );
};

export default ServiceDetailPage;
